import { useCallback, useMemo, useState } from 'react'
import type { PickableRepository } from './repository-pick-list.tsx'

/**
 * How many repositories one create or add can carry, matching the API's all-or-nothing membership
 * call. At the cap the unchecked rows go dead rather than the Confirm button, so the reader is
 * stopped where the overshoot would happen.
 */
export const SELECTION_CAP = 100

/** Shown on the unchecked rows once {@link SELECTION_CAP} is reached. */
export const SELECTION_CAP_REASON = 'Up to 100 at a time.'

/**
 * The filter-and-pick state shared by the create and add dialogs: a free-text filter that narrows the
 * shown candidates by name as it is typed, plus the picked set itself. Membership is manual — the
 * filter only changes what is on screen, never what is selected — so nothing is pre-checked.
 */
export const useRepositoryPicker = ({ candidates }: { candidates: PickableRepository[] }) => {
  const [filter, setFilter] = useState('')
  const [pickedIds, setPickedIds] = useState<ReadonlySet<string>>(new Set())

  const shownCandidates = useMemo(() => {
    const query = filter.trim().toLowerCase()
    if (query === '') {
      return candidates
    }
    return candidates.filter(candidate => candidate.fullName.toLowerCase().includes(query))
  }, [candidates, filter])

  // Bounded here rather than in each dialog, so create and add cannot drift apart on the cap.
  // Unchecking always works, even at the cap; only the next check is refused.
  const toggle = useCallback((repositoryId: string) => {
    setPickedIds(current => {
      const next = new Set(current)
      if (next.has(repositoryId)) {
        next.delete(repositoryId)
        return next
      }
      if (next.size >= SELECTION_CAP) {
        return current
      }
      next.add(repositoryId)
      return next
    })
  }, [])

  const clearPicked = useCallback(() => setPickedIds(new Set()), [])

  return {
    filter,
    setFilter,
    isFiltering: filter.trim() !== '',
    shownCandidates,
    pickedIds,
    isAtSelectionCap: pickedIds.size >= SELECTION_CAP,
    toggle,
    clearPicked,
  }
}
